import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * Data Vault & PII.
 *
 * The register of what personal data this system holds, a check that the schema actually
 * does what the register claims, and the log of people who asked about theirs.
 *
 * Nothing here carries a personal VALUE. Every number is a row count — "how many voters
 * have a phone on record" is answerable without a single phone number leaving the database.
 */

export interface PiiFieldResponse {
  table: string
  column: string
  classification: 'PUBLIC' | 'INTERNAL' | 'CONFIDENTIAL' | 'RESTRICTED'
  piiType: string
  protection: string
  retentionDays: number | null
  consentRequired: boolean
  authorities: string[]
  note: string | null
  populatedRows: number | null
  tableRows: number | null
  /** False when the drift check contradicts what the register claims. */
  verified: boolean
}

export interface VaultInventory {
  fields: PiiFieldResponse[]
  totalFields: number
  totalPopulatedRows: number
  consentRequiredFields: number
  tablesCovered: number
  byClassification: Record<string, number>
  byProtection: Record<string, number>
  byPiiType: Record<string, number>
  unverifiedFields: number
}

export interface DriftFinding {
  code: string
  severity: 'HIGH' | 'MEDIUM' | string
  table: string
  column: string
  detail: string
}

export interface DriftReport {
  declaredFields: number
  tablesInspected: number
  findings: number
  highSeverity: number
  details: DriftFinding[]
}

export interface DsarRequest {
  id: string
  reference: string
  kind: string
  status: string
  subjectVoterId: string | null
  subjectName: string | null
  subjectNote: string | null
  outcomeNote: string | null
  refusedReason: string | null
  exportFileId: string | null
  receivedAt: string
  dueAt: string
  completedAt: string | null
  overdue: boolean
  daysRemaining: number
}

export interface SubjectFootprint {
  voterId: string | null
  foundOnRegister: boolean
  entries: {
    table: string
    describes: string
    rows: number
    /** False for evidence that belongs to a process, not to the subject. */
    erasable: boolean
    retentionNote: string | null
  }[]
  totalRows: number
}

export const vaultInventory = () => api.get<VaultInventory>('/vault/inventory')

export const vaultDrift = () => api.get<DriftReport>('/vault/drift')

export const listDsar = (openOnly = false) =>
  api.get<DsarRequest[]>(`/vault/dsar${qs({ openOnly })}`)

export const recordDsar = (body: {
  nationalId: string; kind: string; subjectNote?: string | null
}) => api.post<DsarRequest>('/vault/dsar', body)

export const dsarFootprint = (id: string) =>
  api.get<SubjectFootprint>(`/vault/dsar/${id}/footprint`)

export const fulfilDsar = (id: string, outcomeNote?: string) =>
  api.post<DsarRequest>(`/vault/dsar/${id}/fulfil`, { outcomeNote: outcomeNote ?? null })

export const refuseDsar = (id: string, reason: string) =>
  api.post<DsarRequest>(`/vault/dsar/${id}/refuse`, { reason })

/** Scoped and audited — evidence tables are never cascaded. */
export const eraseDsar = (id: string) => api.post<DsarRequest>(`/vault/dsar/${id}/erase`)
